'use client';

import React, { FC } from 'react';

import { useSearchParams } from 'next/navigation';

import classNames from 'classnames';

import { TypeProduct, TypeShipmentList } from '@/core/generated-types';

import { ShopifyProduct } from '@/lib/shopify';

import { createImageEntry } from '@/utils/createImageEntry';
import { getVariantId, getVariantImageSet } from '@/utils/helpers';
import { Translations } from '@/utils/translations/generateTranslationObject';

import { CarouselProducts } from '../Carousel';
import { ProductAddToCart } from '../ProductAddToCart';
import { ProductOptionsAddToCart } from '../ProductOptionsAddToCart';
import { TypeSubscriptionPlanInstallmentsResponse } from '@/lib/api/subscriptionManager/types';
import { TypeTariffSettings } from '../types';

export interface ProductDetailsHeaderProps {
  shopifyProduct: ShopifyProduct | null;
  shopifyAdditionalProducts: ShopifyProduct[];
  product: TypeProduct;
  additionalProducts?: TypeProduct[];
  alternativeProduct?: TypeProduct;
  shopifyAlternativeProduct?: ShopifyProduct;
  t?: Translations | null;
  shipmentList?: TypeShipmentList;
  enableStickyCarousel?: boolean;
  subscriptionPlanInstallments?: TypeSubscriptionPlanInstallmentsResponse;
  tariffSettings?: TypeTariffSettings;
}

const ProductDetailsHeader: FC<ProductDetailsHeaderProps> = ({
  shopifyProduct,
  shopifyAdditionalProducts,
  product,
  additionalProducts,
  alternativeProduct,
  shopifyAlternativeProduct,
  t,
  shipmentList,
  enableStickyCarousel,
  subscriptionPlanInstallments,
  tariffSettings,
}) => {
  const searchParams = useSearchParams();

  const initialProductImages = product.fields.productImages || [];

  const hasVariants = shopifyProduct
    ? shopifyProduct.variants.length > 1
    : false;

  const hasProductOptions =
    shopifyProduct &&
    additionalProducts &&
    shopifyAdditionalProducts.length > 0;

  let allMedia;

  const defaultVariantId = getVariantId(shopifyProduct?.variants?.[0]);

  const variantId = searchParams?.get('variant') || defaultVariantId;

  const variantImageSet = getVariantImageSet(product, variantId);

  const hasVariantImageSets = variantImageSet?.length;

  if (hasVariantImageSets) {
    allMedia = [...variantImageSet, ...initialProductImages];
  } else {
    allMedia =
      hasVariants && shopifyProduct
        ? [
            ...initialProductImages,
            ...shopifyProduct.variants.map(createImageEntry),
          ]
        : initialProductImages;
  }

  return (
    <div className="mx-auto flex flex-col md:container md:flex-row lg:max-w-[1200px]">
      <div
        className={classNames('md:w-7/12', {
          'md:h-fit md:sticky md:top-24': enableStickyCarousel,
        })}
      >
        <CarouselProducts
          name="pdp hero carousel"
          id={product?.sys.id}
          components={allMedia}
          key={hasVariantImageSets ? variantId : undefined}
        />
      </div>
      <div className="md:w-5/12">
        {hasProductOptions ? (
          <ProductOptionsAddToCart
            t={t || {}}
            products={[product, ...additionalProducts]}
            shopifyProducts={[shopifyProduct, ...shopifyAdditionalProducts]}
            shipmentList={shipmentList}
            alternativeProduct={alternativeProduct}
            shopifyAlternativeProduct={shopifyAlternativeProduct}
            subscriptionPlanInstallments={subscriptionPlanInstallments}
            tariffSettings={tariffSettings}
          />
        ) : (
          product &&
          shopifyProduct && (
            <ProductAddToCart
              shopifyProduct={shopifyProduct}
              product={product}
              isVariantSwitcherDisplayed={hasVariants}
              subscriptionPlanInstallments={subscriptionPlanInstallments}
              tariffSettings={tariffSettings}
            />
          )
        )}
      </div>
    </div>
  );
};

export default React.memo(ProductDetailsHeader);
